import { useEffect, useState } from 'react';
import { mapi } from './api';
import { kuukausiNimi, raha } from '../lib/format';
import {
  Button, Card, Cards, Chip, Empty, Hint, LinkButton, Muted, Panel, Table, TableWrap, Toolbar, Warn,
} from '../ui/primitives';
import type { BillingData, BillingRow } from './types';

const TILA_FI: Record<string, string> = {
  laskutettu: 'Avoin',
  maksettu: 'Maksettu',
  virhe: 'Veloitus epäonnistui',
};

export function BillingView() {
  const [d, setD] = useState<BillingData | null>(null);
  const [kuukausi, setKuukausi] = useState<string | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [kesken, setKesken] = useState<number | null>(null);

  const lataa = async (kk: string | null) => {
    const r = await mapi<BillingData>('/api/master/billing'
      + (kk ? `?month=${encodeURIComponent(kk)}` : ''));
    setD(r);
    setVirhe(null);
  };

  useEffect(() => {
    let peruttu = false;
    (async () => {
      try {
        const r = await mapi<BillingData>('/api/master/billing'
          + (kuukausi ? `?month=${encodeURIComponent(kuukausi)}` : ''));
        if (!peruttu) { setD(r); setVirhe(null); }
      } catch (err) {
        if (!peruttu) setVirhe(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { peruttu = true; };
  }, [kuukausi]);

  const veloita = async (r: BillingRow) => {
    if (!r.invoice) return;
    setKesken(r.id);
    setIlmoitus(null);
    try {
      await mapi(`/api/master/invoices/${r.invoice.id}/charge`, 'POST', {});
      await lataa(d?.month || null);
      setIlmoitus(`Lasku ${r.invoice.number} veloitettu.`);
    } catch (err) {
      setIlmoitus(err instanceof Error ? err.message : String(err));
    }
    setKesken(null);
  };

  if (virhe) return <Warn>{virhe}</Warn>;
  if (!d) return <Empty>Ladataan…</Empty>;

  // Yhteissummat esitetään euroissa; yksittäinen rivi omassa valuutassaan
  const val = 'EUR' as const;

  return (
    <>
      <Toolbar>
        <label style={{ fontSize: 13.5, color: '#5E5E58' }}>
          Kuukausi
          <select value={d.month} onChange={e => setKuukausi(e.target.value)} style={{ marginLeft: 6 }}>
            {d.months.map(m => <option key={m} value={m}>{kuukausiNimi(m)}</option>)}
          </select>
        </label>
        <span style={{ flex: 1 }} />
        <Button onClick={() => {
          location.href = `/api/master/billing.xlsx?month=${encodeURIComponent(d.month)}`;
        }}>
          ⬇ Vie Exceliin
        </Button>
      </Toolbar>
      {ilmoitus && <Warn style={{ marginBottom: 10 }}>{ilmoitus}</Warn>}

      <Cards>
        <Card $korostus>
          <div className="num">{raha(d.total.amount, val)}</div>
          <div className="lbl"><strong>Laskutetaan yhteensä</strong></div>
        </Card>
        <Card>
          <div className="num">{raha(d.total.unbilled, val)}</div>
          <div className="lbl">Ei laskutettu, AI maksutta</div>
        </Card>
        <Card>
          <div className="num">{raha(d.total.cost, val)}</div>
          <div className="lbl">Toteutunut AI-kulu</div>
        </Card>
        <Card>
          <div className="num">{raha(d.total.margin, val)}</div>
          <div className="lbl">Kate</div>
        </Card>
        <Card>
          <div className="num">{d.total.calls.toLocaleString('fi-FI')}</div>
          <div className="lbl">AI-toimintoa</div>
        </Card>
      </Cards>

      <h2>Asiakkaittain — {kuukausiNimi(d.month)}</h2>
      {d.rows.length ? (
        <TableWrap>
          <Table $eiKlikattava>
            <thead>
              <tr>
                <th>Asiakas</th><th>Paketti</th><th>AI-toimintoja</th><th>Verkkohakuja</th>
                <th>Kulu</th><th>Kerroin</th><th>AI-kulutus</th><th>Ei laskutettu</th><th>Kk-maksu</th>
                <th>Laskutetaan</th><th>Kate</th><th>Maksutapa</th><th>Lasku</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map(r => (
                <tr key={r.id} style={{ opacity: r.active ? 1 : 0.6 }}>
                  <td>
                    <strong>{r.name}</strong>
                    {!r.saatavilla && <><br /><Muted style={{ color: '#A63D24' }}>kantaa ei löydy</Muted></>}
                  </td>
                  <td>{r.plan || '—'}</td>
                  <td>{r.calls}</td>
                  <td>{r.web_searches}</td>
                  <td>{raha(r.cost, r.currency)}</td>
                  <td>×{r.multiplier}</td>
                  <td>{raha(r.usage_price, r.currency)}</td>
                  <td>
                    {r.ai_free
                      ? <strong>{raha(r.unbilled, r.currency)}</strong>
                      : <Muted>{raha(0, r.currency)}</Muted>}
                  </td>
                  <td>{raha(r.monthly_fee, r.currency)}</td>
                  <td><strong>{raha(r.amount, r.currency)}</strong></td>
                  <td>{raha(r.margin, r.currency)}</td>
                  <td>
                    {r.billing_ready ? (
                      <>
                        <Chip $bg="#5C6B54">{r.maksutapa === 'kortti' ? 'Kortti' : 'Lasku'}</Chip>
                        {r.invoice_channel && (
                          <><br /><Muted style={{ fontSize: 12 }}>{r.invoice_channel}</Muted></>
                        )}
                        {r.payment_terms != null && r.maksutapa !== 'kortti' && (
                          <><br /><Muted style={{ fontSize: 12 }}>{r.payment_terms} pv netto</Muted></>
                        )}
                      </>
                    ) : (
                      <>
                        <Chip $bg="#A63D24">Puutteellinen</Chip>
                        <br />
                        <Muted style={{ fontSize: 12 }}>{(r.billing_missing || []).join(', ')}</Muted>
                      </>
                    )}
                  </td>
                  <td>
                    {r.invoice ? (
                      <>
                        <strong>{r.invoice.number}</strong>
                        <br />
                        <Muted style={{ fontSize: 12 }}>
                          {TILA_FI[r.invoice.status] || r.invoice.status}
                          {' · '}{raha(r.invoice.gross, r.invoice.currency as 'EUR' | 'USD')} sis. ALV
                        </Muted>
                        {r.invoice.virhe && (
                          <><br /><Muted style={{ fontSize: 12, color: '#A63D24' }}>{r.invoice.virhe}</Muted></>
                        )}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                          <LinkButton $small href={`/api/master/invoices/${r.invoice.id}`} target="_blank" rel="noreferrer">
                            Avaa
                          </LinkButton>
                          {r.invoice.stripe_pdf && (
                            <LinkButton $small href={r.invoice.stripe_pdf} target="_blank" rel="noreferrer">
                              PDF
                            </LinkButton>
                          )}
                          {r.invoice.payment_method === 'kortti' && r.invoice.status !== 'maksettu' && (
                            <Button $small $variant="primary" disabled={kesken === r.id} onClick={() => void veloita(r)}>
                              Velota
                            </Button>
                          )}
                        </div>
                      </>
                    ) : d.month >= new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Helsinki' }).slice(0, 7) ? (
                      <Muted>Lähtee kuun vaihteessa</Muted>
                    ) : r.amount > 0 ? (
                      <Muted>{r.billing_ready ? 'Lähtee automaattisesti' : 'Odottaa laskutustietoja'}</Muted>
                    ) : (
                      <Muted>Ei laskutettavaa</Muted>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : <Empty>Ei asiakkaita.</Empty>}

      <Panel style={{ marginTop: 18 }}>
        <Hint style={{ margin: 0 }}>
          <strong>Kuukausilasku.</strong> Päättynyt kuukausi lähtee itsestään.
          Kortti veloitetaan, lasku lähtee asiakkaan sähköpostiin. Tässä näkyy
          maksutapa ja se, onko lasku maksettu. Excel-vienti jää kirjanpidon
          yhteenvedoksi. Asiakas näkee omassa Laskutus-välilehdessään laskunsa
          ja niiden tilan — ei toteutunutta kulua, kerrointa eikä katetta.
        </Hint>
      </Panel>
    </>
  );
}
